import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import ENDPOINTS from "../../config/apiUrls";
import axiosInstance from "../../config/axiosInstance";
import _ from 'lodash';
import { formatTimestamp, getOwnerEmail, pick } from "../../helpers/utils";
import qs from 'qs';
import Swal from "sweetalert2";
import { convertTimeToUtc } from "../../helpers/date-time";
import { HUMAN_TIMEZONES } from "../../constants";
import moment from "moment";
import { purgeCloudflareCache } from "../../helpers/cloudflareCache";


export const getPollsList = createAsyncThunk(
    "polls/getPollsList",
    async ({ page = 1, limit = 10 }, { getState, rejectWithValue }) => {
        const { auth } = getState();
        const roomId = auth?.user?.attributes?.stationChatId;
        try {

            const { data: pollsData } = await axiosInstance.get(ENDPOINTS.POLLS.LIST, {
                params: {
                    'filter[ownerId]': roomId,
                    'page[number]': page,
                    'page[size]': limit
                }
            });

            const userIdsToFetch = new Set();

            const polls = await Promise.all(
                pollsData.data.map(async (poll) => {
                    const relations = poll.relations.answers.data;
                    const { data: answersResponse } = await axiosInstance.get(`${ENDPOINTS.POLLS.ANSWERS}?id=${poll.id}`);

                    const updatedAnswers = answersResponse.meta.answers.map((ans) => {
                        return {
                            ...ans,
                            users: answersResponse.meta.users
                                .filter((usr) => usr.answerId === ans.id)
                                .map((filteredUser) => filteredUser.userId),
                        };
                    });

                    for (const answer of updatedAnswers) {
                        const answerRelation = _.find(relations, { id: answer.id });
                        if (answerRelation) {
                            answerRelation.attributes.votesCount = answer.votesCount;
                            answerRelation.votesCount = answer.votesCount;
                            answerRelation.attributes.userParticipated = answer.users.length ? answer.users : [];
                            answer.users.forEach(userIdsToFetch.add, userIdsToFetch);
                        }
                    }

                    return poll;
                })
            );

            const uniqueUserIds = Array.from(userIdsToFetch);
            let usersData = [];

            if (uniqueUserIds.length > 0) {
                const { data } = await axiosInstance.post(ENDPOINTS.CHAT.PARTICIPANT_BY_ID, {
                    roomId,
                    id: uniqueUserIds,
                });
                usersData = data;
            }

            const processedPolls = await Promise.all(
                polls.map(async (poll) => {
                    if (poll.relations.answers.data) {
                        poll.relations.answers.data = await Promise.all(
                            poll.relations.answers.data.map(async (answer) => {
                                const answerRelation = _.find(poll.relations.answers.data, { id: answer.id });
                                if (answerRelation) {
                                    answerRelation.attributes.votesCount = answer.votesCount;

                                    answerRelation.attributes.userParticipated = answerRelation.attributes.userParticipated || [];
                                    answerRelation.attributes.userParticipated = usersData.data?.filter((user) =>
                                        answerRelation.attributes.userParticipated.includes(user.id)
                                    );
                                }
                                return answerRelation;
                            })
                        );
                    }
                    return poll;
                })
            );
            return { data: processedPolls, meta: pollsData.meta };
        } catch (error) {
            console.log(error.message, 'errorerrorerrorerrorerror')
            return rejectWithValue(error.response?.data || "Error loading messages");
        }
    }
);


export const createPoll = createAsyncThunk(
    'polls/createPoll',
    async ({ data, socket }, { getState, rejectWithValue }) => {
        try {
            const { ownerEmail, stationChatId } = getState().auth.user.attributes;
            const preparedData = {
                ...data,
                answers: data.answers.filter(ans => typeof ans === 'string' && ans.trim() !== ''),
            };

            const scheduledStartTime = data.startDate
                ? convertTimeToUtc(data.startDate, data.startTime, HUMAN_TIMEZONES[data.timezone])
                : new Date();

            const scheduledEndTime = data.endDate
                ? convertTimeToUtc(data.endDate, data.endTime, HUMAN_TIMEZONES[data.timezone])
                : new Date();

            const pollResponse = await axiosInstance.post(ENDPOINTS.POLLS.CREATE, {
                title: data.title,
                scheduledStartTime,
                scheduledEndTime,
                tz: data.timezone || null,
                isScheduled: data.startDate ? 1 : 0,
                ownerId: stationChatId,
                displayData: JSON.stringify({
                    startTime: data.startTime || '',
                    endTime: data.endTime || '',
                    startDate: data.startDate || '',
                    endDate: data.endDate || '',
                }),
                minUserAnswersCount: 1,
                maxUserAnswersCount: 1,
                ownerEmail: data.startDate ? ownerEmail : null,
                image: data.image ? data.image : "",
                description: data.description ? data.description : "",
            });

            const pollId = pollResponse.data.data.id;

            await Promise.all(
                preparedData.answers.map((answer, idx) =>
                    axiosInstance.post(ENDPOINTS.POLLS.ANSWER_CREATE, {
                        pollId,
                        title: answer,
                        position: idx,
                    })
                )
            );

            const finalPoll = await axiosInstance.get(`${ENDPOINTS.POLLS.GET_BY_ID}?id=${pollId}`);
            if (socket) {
                socket.emit('broadcast.pollCreated' + stationChatId, { data: finalPoll.data.data });
            }
            
            // Purge Cloudflare cache for users/list URL
            const { stationGroup } = getState().auth.user.attributes;
            if (stationGroup) {
                await purgeCloudflareCache(stationGroup);
            }
            
            return finalPoll.data.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const updatePoll = createAsyncThunk(
    'polls/update',
    async ({ data }, { getState, rejectWithValue }) => {
        try {
            const preparedData = {
                ...data,
                answers: data.answers
            };
            const { ownerEmail } = getState().auth.user.attributes;
            let existingPoll = getState().poll.selectedPoll;
            let updatedPollState = {};
            preparedData.answers = preparedData.answers.map((answer, idx) => ({
                answer,
                position: idx,
            }));

            const updateFields = { ownerEmail: null };

            if (preparedData.title !== existingPoll?.attributes?.title) {
                updateFields.title = preparedData.title;
            }
            if (existingPoll?.attributes?.isScheduled) {
                updateFields.ownerEmail = ownerEmail;

                const displayData = JSON.parse(existingPoll?.attributes?.displayData);

                if (preparedData.startDate != displayData.startDate || preparedData.startTime != displayData.startTime) {
                    updateFields.scheduledStartTime = preparedData.startDate
                        ? convertTimeToUtc(preparedData.startDate, preparedData.startTime, HUMAN_TIMEZONES[preparedData.timezone])
                        : new Date();
                }

                if (preparedData.endDate != displayData.endDate || preparedData.endTime != displayData.endTime) {
                    updateFields.scheduledEndTime = preparedData.endDate
                        ? convertTimeToUtc(preparedData.endDate, preparedData.endTime, HUMAN_TIMEZONES[preparedData.timezone])
                        : new Date();
                }
            }

            if (preparedData.timezone !== existingPoll?.attributes?.tz) {
                updateFields.tz = preparedData.timezone || null;
            }

            const newDisplayData = JSON.stringify({
                startTime: preparedData.startTime || '',
                endTime: preparedData.endTime || '',
                startDate: preparedData.startDate || '',
                endDate: preparedData.endDate || '',
            });

            if (newDisplayData !== existingPoll?.attributes?.displayData) {
                updateFields.displayData = newDisplayData;
            }

            if (preparedData.image !== existingPoll?.attributes?.image) {
                updateFields.image = preparedData.image;
            }

            if (preparedData.description !== existingPoll?.attributes?.description) {
                updateFields.description = preparedData.description;
            }

            if (Object.keys(updateFields).length > 0) {
                let resp = await axiosInstance.post(ENDPOINTS.POLLS.UPDATE, {
                    id: existingPoll.id,
                    ...updateFields,
                });
                updatedPollState = resp.data.data;
            }

            // Handle answer deletions
            const answersToDelete = existingPoll.relations.answers.data
                .filter(answer => !preparedData.answers.some(it => it.answer?.id === answer.id));

            for (const answer of answersToDelete) {
                await axiosInstance.post(ENDPOINTS.POLLS.ANSWER_DELETE, { id: answer.id });
            }

            // Handle answer updates/creations
            for (const answer of preparedData.answers) {
                const existing = existingPoll.relations.answers.data.find(it => it.id === answer.answer.id);

                if (!answer.answer.id) {
                    await axiosInstance.post(ENDPOINTS.POLLS.ANSWER_CREATE, {
                        pollId: existingPoll.id,
                        title: answer.answer.title,
                        position: answer.position,
                    });
                } else if (!existing || existing.attributes.title !== answer.answer.title || existing.attributes.position !== answer.position) {
                    await axiosInstance.post(ENDPOINTS.POLLS.ANSWER_UPDATE, {
                        id: existing.id,
                        title: answer.answer.title,
                        position: answer.position
                    });
                }
            }

            let { data: answersResponse } = await axiosInstance.get(`${ENDPOINTS.POLLS.ANSWERS}?id=${existingPoll.id}`);

            updatedPollState.relations.answers.data = answersResponse.data;

            // Purge Cloudflare cache for users/list URL
            const { stationGroup } = getState().auth.user.attributes;
            if (stationGroup) {
                await purgeCloudflareCache(stationGroup);
            }

            return updatedPollState;
        } catch (err) {
            console.log(err.message, '---');
            return rejectWithValue(err.response?.data || 'Update failed');
        }
    }
);


export const deletePoll = createAsyncThunk(
    "polls/deletePoll",
    async ({ id, socket }, { getState, rejectWithValue }) => {
        try {
            await axiosInstance.post(ENDPOINTS.POLLS.DELETE, { id });

            Swal.fire("Deleted!", "Poll has been Successfully deleted.", "success");
            const { stationGroup } = getState().auth.user.attributes;
            if (stationGroup) {
                await purgeCloudflareCache(stationGroup);
            }
            return { id, socket };
        } catch (error) {
            console.log(error.message, 'errorerrorerrorerrorerror')
            return rejectWithValue(error.response?.data || "Error loading messages");
        }
    }
);

export const endPoll = createAsyncThunk(
    "polls/endPoll",
    async ({ id }, { getState, rejectWithValue }) => {
        try {
            const { auth } = getState();
            const ownerEmail = getOwnerEmail(auth.user);

            await axiosInstance.post(ENDPOINTS.POLLS.STOP, { id, ownerEmail: ownerEmail });
            const { stationGroup } = getState().auth.user.attributes;
            if (stationGroup) {
                await purgeCloudflareCache(stationGroup);
            }
            return { id, newState: 2 };
        } catch (error) {
            console.log(error.message, 'errorerrorerrorerrorerror')
            return rejectWithValue(error.response?.data || "Error loading messages");
        }
    }
);

export const archivePoll = createAsyncThunk(
    "polls/archivePoll",
    async ({ id }, { getState, rejectWithValue }) => {
        try {
            const { auth } = getState();
            const ownerEmail = getOwnerEmail(auth.user);

            await axiosInstance.post(ENDPOINTS.POLLS.ARCHIVE, { id });
            const { stationGroup } = getState().auth.user.attributes;
            if (stationGroup) {
                await purgeCloudflareCache(stationGroup);
            }
            return { id, newState: 4 };
        } catch (error) {
            console.log(error.message, 'errorerrorerrorerrorerror')
            return rejectWithValue(error.response?.data || "Error loading messages");
        }
    }
);

export const startPoll = createAsyncThunk(
    "polls/startPoll",
    async ({ id }, { getState, rejectWithValue }) => {
        try {
            const { auth } = getState();
            const ownerEmail = getOwnerEmail(auth.user);

            await axiosInstance.post(ENDPOINTS.POLLS.START, { id, ownerEmail: ownerEmail });

            const { stationGroup } = getState().auth.user.attributes;
            if (stationGroup) {
                await purgeCloudflareCache(stationGroup);
            }
            return { id, newState: 1 };
        } catch (error) {
            console.log(error.message, 'errorerrorerrorerrorerror')
            return rejectWithValue(error.response?.data || "Error loading messages");
        }
    }
);

export const exportPollParticipants = createAsyncThunk(
    'contests/exportParticipants',
    async ({ id, usersList = [] }, { getState }) => {
        const { auth } = getState();
        const roomId = auth?.user?.attributes?.stationChatId;
        let userIds = [];
        if (usersList.length == 0) {
            const { data } = await axiosInstance.get(`${ENDPOINTS.POLLS.USERS_LIST}?id=${id}`);

            userIds = data.data.map(user => user.id);
            if (userIds.length === 0) return [];
        } else {
            userIds = usersList;
        }

        const { data: userMetaData } = await axiosInstance.get(ENDPOINTS.USERS.UPDATE_PROFILE, {
            params: { id: userIds },
            paramsSerializer: params => qs.stringify(params, { indices: false }),
        });
        const { data: chatParticipants } = await axiosInstance.post(ENDPOINTS.CHAT.PARTICIPANT_BY_ID, {
            roomId,
            id: userIds,
        });

        return (Array.isArray(userMetaData?.data) ? userMetaData?.data : [userMetaData?.data])?.map(user => {
            if (!user) return null; // Handle cases where userMetaData.data is null/undefined

            const match = chatParticipants?.find(firstUser => firstUser.id == user.id);
            console.log(match);
            return {
                name: match?.user?.name || user.attributes.firstName + ' ' + user.attributes.lastName,
                userId: user.id || '',
                email: match?.user?.email || user.attributes.email || 'N/A',
                DOB: match?.user?.birthday != null ? moment(match?.user?.birthday).format("MM/DD/YYYY") : "N/A",
                Gender: match?.user?.gender ?? "N/A",
                avatar: match?.user?.avatar || user.attributes.avatar || null,
                zipcode: match?.user?.zipcode || user.attributes.zipcode || 'N/A',
                joinedAt: formatTimestamp(user.attributes.created),
                activity: match?.user?.lastActivityAt ? formatTimestamp(match.user.lastActivityAt) : 'N/A',
                status: match?.user?.bannedAt ? 'Blocked' : 'Active',
                role: match?.user?.roles?.length ? match.user.roles.join(' ') : 'Participant',
                extraInfo: user.attributes


            };
        }).filter(Boolean);
    }
);

const pollSlice = createSlice({
    name: "poll",
    initialState: {
        polls: [],
        filteredPolls: [],
        selectedPoll: null,
        activePoll: null,
        isLoading: false,
        isSearching: false,
        totalRecordsReach: false,
        meta: {},
        isModelOpen: false,
        pollId: null,
        isArchiving: false,
        isUpdating: false,
        isOptionalModelOpen: false,
        optonalInfo: null
    },
    reducers: {
        openParticipantModel: (state, action) => {
            state.isModelOpen = true;
            state.pollId = action.payload;

        },
        closeParticipantModel: (state) => {
            state.isModelOpen = false;
            state.pollId = null;

        },
        openOptionalInfoModelPoll: (state, action) => {
            state.isOptionalModelOpen = true;
            state.optonalInfo = action.payload;

        },
        closeOptionalInfoModelPoll: (state) => {
            state.isOptionalModelOpen = false;
            state.optonalInfo = null;


        },
        getPollById: (state, action) => {
            const pollId = action.payload.id;
            const selected = state.polls.find((poll) => poll.id === pollId);
            if (selected) {
                state.selectedPoll = selected; // full poll object
            } else {
                state.selectedPoll = null; // or handle not found case
            }
        },

        removeSelectedPoll: (state) => {
            state.selectedPoll = null
        },
        updateSearchingAction: (state, action) => {
            state.isSearching = action.payload;

        },
        filterPolls: (state, action) => {
            const searchQuery = action.payload.toLowerCase();

            state.filteredPolls = state.polls.filter(
                (poll) => poll?.attributes?.title?.toLowerCase().includes(searchQuery)
            );
        },
        resetPollState: (state) => {
            state.polls = [];
            state.filteredPolls = [];
            state.selectedPoll = null;
            state.activePoll = null;
            state.isLoading = false;
            state.isSearching = false;
            state.totalRecordsReach = false;
            state.meta = {};
            state.isModelOpen = false;
            state.pollId = null;
            state.isArchiving = false;
            state.isUpdating = false;
            state.isOptionalModelOpen = false;
            state.optonalInfo = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getPollsList.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(getPollsList.fulfilled, (state, action) => {
                state.isLoading = false;
                const filteredMessages = action.payload.data;
                const page = action.meta?.arg?.page ?? 1;

                if (page === 1) {
                    state.polls = filteredMessages ?? [];
                    state.filteredPolls = filteredMessages ?? [];
                    state.totalRecordsReach = false;
                } else {
                    state.polls = [...state.polls, ...(filteredMessages ?? [])];
                    state.filteredPolls = [...state.filteredPolls, ...(filteredMessages ?? [])];
                }

                state.meta = action?.payload?.meta ?? state.meta;
                if (state.meta?.rowCount != null && state.polls.length >= state.meta.rowCount) {
                    state.totalRecordsReach = true;
                }
                state.activePoll =
                    state.polls.find(poll => poll.attributes.state === 1) ||
                    state.polls.find(poll => poll.attributes.state === 2);
            })
            .addCase(deletePoll.fulfilled, (state, action) => {
                let { id, socket } = action.payload;
                state.polls = state.polls.filter((poll) => poll.id !== id);
                state.filteredPolls = state.filteredPolls.filter((poll) => poll.id !== id);

                if (socket) {
                    socket.emit('broadcast.pollDeleted', { data: { id } });
                }
                state.activePoll =
                    state.polls.find(poll => poll.attributes.state === 1) ||
                    state.polls.find(poll => poll.attributes.state === 2);
            })
            .addCase(endPoll.pending, (state) => {
                state.isUpdating = true;
                state.error = null;
            })
            .addCase(endPoll.fulfilled, (state, action) => {
                let { id, newState } = action.payload;

                state.polls = state.polls.map(poll =>
                    poll.id === id
                        ? { ...poll, attributes: { ...poll.attributes, state: newState } }
                        : poll
                );
                state.filteredPolls = state.filteredPolls.map(poll =>
                    poll.id === id
                        ? { ...poll, attributes: { ...poll.attributes, state: newState } }
                        : poll
                );

                state.activePoll =
                    state.polls.find(poll => poll.attributes.state === 1) ||
                    state.polls.find(poll => poll.attributes.state === 2);
                state.isUpdating = false;
            })
            .addCase(startPoll.pending, (state) => {
                state.isUpdating = true;
                state.error = null;
            })
            .addCase(startPoll.fulfilled, (state, action) => {
                let { id, newState } = action.payload;

                state.polls = state.polls.map(poll =>
                    poll.id === id
                        ? { ...poll, attributes: { ...poll.attributes, state: newState } }
                        : poll
                );
                state.filteredPolls = state.filteredPolls.map(poll =>
                    poll.id === id
                        ? { ...poll, attributes: { ...poll.attributes, state: newState } }
                        : poll
                );

                state.activePoll =
                    state.polls.find(poll => poll.attributes.state === 1) ||
                    state.polls.find(poll => poll.attributes.state === 2);
                state.isUpdating = false;

            })
            .addCase(archivePoll.pending, (state) => {
                state.isArchiving = true;
                state.error = null;
            })
            .addCase(archivePoll.fulfilled, (state, action) => {
                let { id, newState } = action.payload;

                state.polls = state.polls.map(poll =>
                    poll.id === id
                        ? { ...poll, attributes: { ...poll.attributes, state: newState } }
                        : poll
                );
                state.filteredPolls = state.filteredPolls.map(poll =>
                    poll.id === id
                        ? { ...poll, attributes: { ...poll.attributes, state: newState } }
                        : poll
                );
                state.activePoll =
                    state.polls.find(poll => poll.attributes.state === 1) ||
                    state.polls.find(poll => poll.attributes.state === 2);
                state.isArchiving = false;

            })
            .addCase(createPoll.fulfilled, (state, action) => {
                const newPoll = action.payload;

                state.polls = [newPoll, ...state.polls];
                state.filteredPolls = [newPoll, ...state.filteredPolls];
                state.activePoll =
                newPoll?.attributes?.state === 1
                  ? newPoll
                  : state.polls.find(poll => poll.attributes.state === 1) ||
                    (newPoll?.attributes?.state === 2 ? newPoll : null) ||
                    state.polls.find(poll => poll.attributes.state === 2);

                state.isArchiving = false;
            })
            .addCase(updatePoll.fulfilled, (state, action) => {
                let updatePollState = action.payload;

                state.polls = state.polls.map(poll =>
                    poll.id === updatePollState.id
                        ? updatePollState
                        : poll
                );
                state.filteredPolls = state.filteredPolls.map(poll =>
                    poll.id === updatePollState.id
                        ? updatePollState
                        : poll
                );
                state.activePoll =
                    state.polls.find(poll => poll.attributes.state === 1) ||
                    state.polls.find(poll => poll.attributes.state === 2);
                state.isArchiving = false;
            });

    },
});
export const { openParticipantModel, closeParticipantModel, updateSearchingAction, filterPolls, getPollById, removeSelectedPoll, openOptionalInfoModelPoll, closeOptionalInfoModelPoll, resetPollState } = pollSlice.actions;

export default pollSlice.reducer;